<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    captureFromFile,
    captureFromVideo,
    captureVideoFrame,
    startCameraPreview,
    stopCameraPreview,
    type CaptureResult
  } from './capture';
  import { detectLips } from '../tracking/faceLandmarker';
  import { analyzeFaceQuality } from '../quality/faceQuality';
  import type { VtoCopy } from '../i18n/vtoCopy';

  type Props = {
    oncapture: (result: CaptureResult) => void;
    copy: VtoCopy['capture'];
  };
  type ScanState = 'idle' | 'warming' | 'good' | 'adjust' | 'blocked' | 'unavailable';
  let { oncapture, copy }: Props = $props();

  let videoEl: HTMLVideoElement | undefined = $state();
  let fileInput: HTMLInputElement | undefined = $state();
  let stream: MediaStream | null = null;
  let status = $state<'idle' | 'starting' | 'ready' | 'error' | 'denied'>('idle');
  let errorMsg = $state('');
  let scanState = $state<ScanState>('idle');
  let scanMessage = $state('');
  let scanDetail = $state('');
  let scanTimer: number | undefined;
  let scanGeneration = 0;
  let mounted = true;

  const scanLabel = $derived(
    scanState === 'good' ? copy.labels.good :
    scanState === 'adjust' ? copy.labels.adjust :
    scanState === 'blocked' ? copy.labels.blocked :
    scanState === 'warming' ? copy.labels.warming :
    scanState === 'unavailable' ? copy.labels.unavailable :
    copy.labels.idle
  );
  const scanTone = $derived(
    scanState === 'good' ? 'border-emerald-300/70 bg-emerald-50 text-emerald-950' :
    scanState === 'adjust' ? 'border-amber-300/70 bg-amber-50 text-amber-950' :
    scanState === 'blocked' ? 'border-brand-accent/30 bg-brand-highlight/70 text-brand-ink' :
    'border-white/25 bg-black/45 text-white'
  );
  const canSnap = $derived(status === 'ready' && scanState !== 'blocked');

  async function enableCamera() {
    if (!videoEl) return;
    status = 'starting';
    errorMsg = '';
    stopScanLoop();
    scanState = 'idle';
    scanMessage = '';
    scanDetail = '';
    try {
      stream = await startCameraPreview(videoEl);
      status = 'ready';
      startScanLoop();
    } catch (err) {
      const name = (err as Error).name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        status = 'denied';
      } else {
        status = 'error';
        errorMsg = (err as Error).message || copy.errors.cameraUnavailable;
      }
    }
  }

  async function snap() {
    if (!videoEl || !canSnap) return;
    try {
      stopScanLoop();
      const result = await captureFromVideo(videoEl);
      stopCameraPreview(stream);
      stream = null;
      oncapture(result);
    } catch (err) {
      status = 'error';
      errorMsg = (err as Error).message || copy.errors.captureFailed;
    }
  }

  async function onFileChosen(e: Event) {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    try {
      const result = await captureFromFile(file);
      stopScanLoop();
      stopCameraPreview(stream);
      stream = null;
      oncapture(result);
    } catch (err) {
      status = 'error';
      errorMsg = (err as Error).message || copy.errors.unreadableImage;
    }
  }

  // Dev-only: one-tap load of a bundled test portrait so we can iterate on
  // the 13 looks without re-taking photos. Tree-shaken out of production.
  const isDev = import.meta.env.DEV;
  const testFaces = [
    { label: 'F1', url: `${import.meta.env.BASE_URL}assets/test-faces/face-1.jpg` },
    { label: 'F2', url: `${import.meta.env.BASE_URL}assets/test-faces/face-2.jpg` },
    { label: 'F3', url: `${import.meta.env.BASE_URL}assets/test-faces/face-3.jpg` },
    { label: 'T1', url: `${import.meta.env.BASE_URL}assets/test-faces/test1.png` },
    { label: 'T2', url: `${import.meta.env.BASE_URL}assets/test-faces/test2.png` },
    { label: 'T3', url: `${import.meta.env.BASE_URL}assets/test-faces/test3.png` },
    { label: 'T4', url: `${import.meta.env.BASE_URL}assets/test-faces/test4.png` },
    { label: 'NM', url: `${import.meta.env.BASE_URL}assets/test-faces/nomakeupface.jpeg` },
    { label: 'NM2', url: `${import.meta.env.BASE_URL}assets/test-faces/nomakeupface2.jpeg` },
    { label: '20', url: `${import.meta.env.BASE_URL}20.png` },
    { label: '30', url: `${import.meta.env.BASE_URL}30.png` },
    { label: '40', url: `${import.meta.env.BASE_URL}40.png` },
    { label: '50', url: `${import.meta.env.BASE_URL}50.png` }
  ];

  async function loadTestFace(url: string) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const fileName = url.split('/').pop() || 'test.jpg';
      const file = new File([blob], fileName, { type: blob.type });
      const result = await captureFromFile(file);
      stopScanLoop();
      stopCameraPreview(stream);
      stream = null;
      oncapture(result);
    } catch (err) {
      status = 'error';
      errorMsg = (err as Error).message || copy.errors.testFaceFailed;
    }
  }

  function stopScanLoop() {
    scanGeneration += 1;
    if (scanTimer !== undefined) {
      window.clearTimeout(scanTimer);
      scanTimer = undefined;
    }
  }

  function queueScan(delay = 900) {
    if (!mounted || status !== 'ready') return;
    const generation = scanGeneration;
    scanTimer = window.setTimeout(() => {
      if (generation === scanGeneration) void runScan(generation);
    }, delay);
  }

  function startScanLoop() {
    stopScanLoop();
    scanState = 'warming';
    scanMessage = copy.scan.startMessage;
    scanDetail = copy.scan.startDetail;
    queueScan(350);
  }

  async function runScan(generation: number) {
    if (!videoEl || status !== 'ready' || generation !== scanGeneration) return;
    let frame: CaptureResult | null = null;
    try {
      frame = await captureVideoFrame(videoEl, { maxSide: 720 });
      const detection = await detectLips(frame.bitmap);
      if (generation !== scanGeneration) return;
      if (!detection) {
        scanState = 'adjust';
        scanMessage = copy.scan.centerFace;
        scanDetail = copy.scan.centerFaceDetail;
        return;
      }

      const report = analyzeFaceQuality(frame.bitmap, detection);
      if (report.blocker) {
        scanState = 'blocked';
        scanMessage = copy.scan.fixBeforeShot;
        scanDetail = copy.scan.centerFaceDetail;
      } else if (report.scenario === 'clean') {
        scanState = 'good';
        scanMessage = copy.scan.readyMessage;
        scanDetail = copy.scan.readyDetail;
      } else {
        scanState = 'adjust';
        scanMessage = copy.scan.improveMessage;
        scanDetail = copy.scan.improveDetail;
      }
    } catch {
      if (generation !== scanGeneration) return;
      scanState = 'unavailable';
      scanMessage = copy.scan.unavailableMessage;
      scanDetail = copy.scan.unavailableDetail;
    } finally {
      try {
        frame?.bitmap.close();
      } catch {
        // Ignore cleanup failures on browsers that already released the bitmap.
      }
      if (generation === scanGeneration) queueScan(scanState === 'good' ? 1300 : 900);
    }
  }

  onMount(() => {
    // Lazy enable — some browsers penalise pages that request camera on load.
  });

  onDestroy(() => {
    mounted = false;
    stopScanLoop();
    stopCameraPreview(stream);
    stream = null;
  });
</script>

<div class="flex w-full flex-col gap-4">
  <div class="relative aspect-[3/4] w-full overflow-hidden rounded-card bg-black">
    <!-- svelte-ignore a11y_media_has_caption -->
    <video
      bind:this={videoEl}
      class="h-full w-full object-cover"
      style="transform: scaleX(-1);"
      playsinline
      muted
      autoplay
    ></video>

    {#if status !== 'ready'}
      <div class="absolute inset-0 flex items-center justify-center bg-black/60 p-6 text-center text-white">
        {#if status === 'idle'}
          <button class="btn-primary" onclick={enableCamera}>
            {copy.camera.enable}
          </button>
        {:else if status === 'starting'}
          <p class="text-sm">{copy.camera.starting}</p>
        {:else if status === 'denied'}
          <div class="space-y-2 text-sm">
            <p class="font-semibold">{copy.camera.deniedTitle}</p>
            <p class="text-white/80">{copy.camera.deniedBody}</p>
          </div>
        {:else if status === 'error'}
          <div class="space-y-2 text-sm">
            <p class="font-semibold">{copy.camera.errorTitle}</p>
            <p class="text-white/80">{errorMsg}</p>
          </div>
        {/if}
      </div>
    {:else}
      <div class="absolute inset-x-3 top-3 rounded-card border px-3 py-2 text-left text-xs shadow-sm backdrop-blur-sm {scanTone}">
        <div class="flex items-center justify-between gap-3">
          <span class="font-semibold">{scanLabel}</span>
          {#if scanState === 'good'}
            <span class="rounded-pill bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white">{copy.camera.ok}</span>
          {/if}
        </div>
        <p class="mt-1 leading-snug">{scanMessage}</p>
        {#if scanDetail}
          <p class="mt-0.5 text-[11px] leading-snug opacity-75">{scanDetail}</p>
        {/if}
      </div>
    {/if}
  </div>

  <div class="flex flex-col gap-2">
    <button
      class="btn-primary w-full"
      onclick={snap}
      disabled={!canSnap}
    >
      {copy.camera.snap}
    </button>
    <button
      class="inline-flex min-h-[44px] w-full items-center justify-center text-sm text-brand-muted underline underline-offset-2"
      onclick={() => fileInput?.click()}
      type="button"
    >
      {copy.camera.upload}
    </button>
    <input
      bind:this={fileInput}
      type="file"
      accept="image/*"
      class="sr-only"
      onchange={onFileChosen}
    />
  </div>

  {#if isDev}
    <div class="flex items-center justify-center gap-2 rounded-card border border-dashed border-brand-accent/30 bg-brand-highlight/20 px-3 py-2 text-xs">
      <span class="text-brand-muted">🧪 dev</span>
      {#each testFaces as tf (tf.url)}
        <button
          class="rounded-pill bg-white px-3 py-1 font-medium text-brand-accent shadow-sm"
          onclick={() => loadTestFace(tf.url)}
        >
          {tf.label}
        </button>
      {/each}
    </div>
  {/if}

  <p class="text-center text-xs text-brand-muted">
    {copy.camera.privacy}
  </p>
</div>
